// Admin — User Management. List of every signed-in user with their roles,
// tool grants, last-active timestamp; expanding a row opens
// <ToolPermissionEditor> for bulk grant/revoke. Auto-provisioned users
// land here on their first SSO sign-in.
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { formatDate } from "../../lib/utils";
import type { AdminUserSummary } from "../../../../shared/types/admin";
import { ToolPermissionEditor } from "./ToolPermissionEditor";

export function UserManagement() {
  const [users, setUsers] = useState<AdminUserSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = async () => {
    try {
      const result = await api<{ users: AdminUserSummary[] }>("/api/admin/users");
      setUsers(result.users);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const saveTools = async (userId: number, toolIds: string[]) => {
    await api(`/api/admin/users/${userId}/tools`, {
      method: "PATCH",
      json: { toolIds },
    });
    await load();
    setEditingId(null);
  };

  return (
    <>
      <div className="bsp-page-header">
        <h1 className="bsp-page-title">User management</h1>
      </div>

      {error && (
        <div className="bsp-card" style={{ borderColor: "var(--bsp-red)" }}>
          <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
        </div>
      )}

      {users === null && !error && <p>Loading users…</p>}

      {users && users.length === 0 && (
        <p style={{ color: "var(--bsp-fg-muted)" }}>
          No users yet. They'll appear here as they sign in for the first time.
        </p>
      )}

      {users && users.length > 0 && (
        <div className="bsp-card" style={{ padding: 0 }}>
          <table className="bsp-table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Name / email</th>
                <th>Roles</th>
                <th>Tools</th>
                <th>Last signed in</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const expanded = editingId === u.id;
                return (
                  <UserRow
                    key={u.id}
                    user={u}
                    expanded={expanded}
                    onToggle={() => setEditingId(expanded ? null : u.id)}
                    onSave={(ids) => saveTools(u.id, ids)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function UserRow({
  user,
  expanded,
  onToggle,
  onSave,
}: {
  user: AdminUserSummary;
  expanded: boolean;
  onToggle: () => void;
  onSave: (toolIds: string[]) => Promise<void>;
}) {
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <>
      <tr style={{ cursor: "pointer" }} onClick={onToggle}>
        <td><Chevron size={14} /></td>
        <td>
          <div style={{ fontWeight: 600 }}>{user.displayName || "—"}</div>
          <div style={{ fontSize: 12, color: "var(--bsp-fg-muted)" }}>{user.email}</div>
        </td>
        <td style={{ fontSize: 12 }}>
          {user.roles.length === 0 ? "—" : user.roles.join(", ")}
        </td>
        <td className="bsp-mono" style={{ fontSize: 12 }}>
          {user.toolIds.length === 0 ? "—" : user.toolIds.join(", ")}
        </td>
        <td className="bsp-mono" style={{ fontSize: 12 }}>
          {formatDate(user.lastSignedIn)}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5}>
            <ToolPermissionEditor
              userId={user.id}
              initialToolIds={user.toolIds}
              onSave={onSave}
              onCancel={onToggle}
            />
          </td>
        </tr>
      )}
    </>
  );
}
